import React, { useCallback, useMemo, useState } from 'react';
import useNonNullContext from '../../../../Hooks/useNonNullContext';
import { SongListContext } from '../../contexts/SongListContext';
import { SelectBoxOptionWithId } from '../../../../ui/DataEntry/SelectBox/SelectBox.types.';
import { SongSimple } from '@round/api';
import styles from './LinkToSongs.module.css';
import { ReactComponent as LinkIcon } from '../../../../assets/LinkIcon.svg';
import cn from 'classnames';
import { Popover } from '@round/ui-kit';
import Card from '../../../../ui/Card/Card';
import OptionsList from '../../../../ui/DataEntry/SelectBox/components/OptionsList/OptionsList';
import Image from '../../../../ui/Image/Image';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import Button from '../../../../ui/Buttons/Button/Button';
import { ReactComponent as AddIcon } from '../../../../assets/Add.svg';
import Checkbox from '../../../../ui/DataEntry/Checkbox/Checkbox';
import CreateSongModal from '../CreateSongModal/CreateSongModal';

type LinkToSongsProps = {
    linkSongs: (songIds: number[]) => void;
};

const LinkToSongs = ({ linkSongs }: LinkToSongsProps) => {
    const [linkToSongRef, setLinkToSongRef] = useState<HTMLButtonElement | null>(null);
    const [isCreateSongModalOpen, setIsCreateSongModalOpen] = useState(false);
    const [selectedSongs, setSelectedSongs] = useState<number[]>([]);

    const { loadMoreSongs, songsCount, songsLoading, songs, search, setSearch } = useNonNullContext(SongListContext);
    const songOptions: SelectBoxOptionWithId[] = useMemo(
        () =>
            songs.map((s) => ({
                id: s.id,
                name: s.title,
                subtitle: s.artists.map((a) => a.name).join(', '),
                picture: s.image ?? '',
            })),
        [songs]
    );

    const handleSongCreated = (song: SongSimple) => {
        linkSongs([song.id]);
    };

    const clearSearchOnPopoverClose = useCallback(
        (show: boolean) => {
            if (!show) {
                setSearch('');
            }
        },
        [setSearch]
    );

    return (
        <>
            <button ref={setLinkToSongRef} className={styles.actionButton}>
                <LinkIcon className={cn(styles.actionButtonIcon, styles.linkButtonIcon)} />
                Link songs
            </button>
            <Popover
                anchorElement={linkToSongRef}
                showOn="click"
                options={{ placement: 'bottom-end' }}
                onShowPopoverChange={clearSearchOnPopoverClose}
            >
                {(setShow) => (
                    <Card className={styles.dropdown}>
                        <OptionsList
                            searchable
                            searchQuery={search}
                            setSearchQuery={setSearch}
                            searchPlaceholder="Search song"
                            options={songOptions}
                            count={songsCount}
                            loading={songsLoading}
                            loadMore={async () => loadMoreSongs()}
                        >
                            {({ item, loading, style }) => (
                                <div
                                    style={style}
                                    className={styles.listItem}
                                    onClick={() => {
                                        if (!item) {
                                            return;
                                        }

                                        setSelectedSongs((songs) => {
                                            if (songs.includes(item.id)) {
                                                return songs.filter((song) => song !== item.id);
                                            }

                                            return songs.concat(item.id);
                                        });
                                    }}
                                >
                                    <Image src={item?.picture} className={styles.listItemImage} />
                                    <div className={styles.listItemDetails}>
                                        <p className={styles.listItemTitle}>{loading ? <Skeleton /> : item?.name}</p>
                                        <p className={styles.listItemSubtitle}>
                                            {loading ? <Skeleton /> : item?.subtitle}
                                        </p>
                                    </div>
                                    <Checkbox value={Boolean(item && selectedSongs.includes(item.id))} />
                                </div>
                            )}
                        </OptionsList>
                        {selectedSongs.length > 0 ? (
                            <Button
                                className={styles.createSongButton}
                                contentClassName={styles.createSongButtonContent}
                                onClick={() => {
                                    linkSongs(selectedSongs);
                                    setShow(false);
                                }}
                            >
                                Link songs
                            </Button>
                        ) : (
                            <Button
                                className={styles.createSongButton}
                                contentClassName={styles.createSongButtonContent}
                                onClick={() => {
                                    setIsCreateSongModalOpen(true);
                                    setShow(false);
                                }}
                            >
                                <AddIcon className={styles.createSongButtonIcon} />
                                <span>Create song</span>
                            </Button>
                        )}
                    </Card>
                )}
            </Popover>

            <CreateSongModal
                isModalOpen={isCreateSongModalOpen}
                closeModal={() => setIsCreateSongModalOpen(false)}
                onCreated={handleSongCreated}
            />
        </>
    );
};

export default LinkToSongs;
